import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { CookiesKeys } from '../constants/cookiesKeys';
import { getErrorMessage } from '../constants/errors';
import { HttpVerbType } from '../constants/httpVerbs';
import getCookieValue from '../utils/getCookieValue';
import { useI18nContext } from './contexts/i18n.context';
import { useModalContext } from './contexts/modal.context';

export interface DefaultApiResponse {
  message: string;
  ok: boolean;
  statusCode: number;
}

type FetchOnly = { resourcePath: string; config?: AxiosRequestConfig };
type DataAttached<T> = FetchOnly & { data: T };

type ServicePayload<T> = {
  requestOptions: FetchOnly | DataAttached<T>;
  type: HttpVerbType;
};

type ApiServiceType = {
  baseUrl: string;
  needToken?: boolean;
  tokenProvider?: () => string | undefined;
};

export const useApiService = () => {
  const { toggleModal, setContentModal, defineTitleModal } = useModalContext();
  const { translate, currentLocale : { locale } } = useI18nContext();

  const navigate = useNavigate();

  const createAxiosInstance = ({ baseUrl, needToken, tokenProvider }: ApiServiceType): AxiosInstance => {
    const axiosInstance = axios.create({ baseURL : baseUrl });

    axiosInstance.interceptors.request.use((config) => {
      const token = tokenProvider ? tokenProvider() : getCookieValue({ cookieKey : CookiesKeys.JWT });
      const trustedDevice = getCookieValue({ cookieKey : CookiesKeys.TRUSTED_DEVICE });

      trustedDevice ? config.headers['x-ingresse-device'] = decodeURIComponent(trustedDevice) : null;

      if (token && needToken) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    axiosInstance.interceptors.response.use(
      (response) => {
        const responseData = response as unknown as {data: {detail: {responseError: string}} };
        try {
          if (responseData.data.detail.responseError) {
            const location = window.location;
            const isRouteTicket = location.pathname.includes('ticket');
            let path = location.href.replace(`${location.origin}/`, '');

            if (!isRouteTicket) {
              path = path.replace(/^[^/]+/, 'tickets');
              const keyError = responseData.data.detail.responseError.toLowerCase().replace(/ /g, '_');
              setContentModal(getErrorMessage(keyError, locale));
              defineTitleModal(translate(['TICKET_TEMPLATE', 'PASSKEY', 'GROUP', 'TEXT_8']));
              toggleModal({ open : true });
              navigate(path);
            }
          }
          return response;
        } catch (error) {
          return response;
        }
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    return axiosInstance;
  };

  const request = useCallback(
    async <T, R>(payload: ServicePayload<T>, apiConfig: ApiServiceType): Promise<R> => {
      const axiosInstance = createAxiosInstance(apiConfig);
      const response = await axiosInstance.request({
        method : payload.type,
        url : payload.requestOptions.resourcePath,
        data : 'data' in payload.requestOptions ? payload.requestOptions.data : undefined,
        ...payload.requestOptions.config,
      });

      return response.data as R;
    },
    []
  );

  return {
    request,
  };
};
